import { CardDetailRows } from "@/modules/card/components/CardDetails/CardDetailRows";
import { CardImage } from "@/modules/card/components/CardDetails/CardImage";
import { useGetCardByIdQuery, useLazyGetCardBalanceQuery } from "@/modules/card/services";
import { AppPaths, CloudflareAsset, SideSheetPosition } from "@/shared/common/enums";
import type { IError } from "@/shared/common/types";
import { CloudFlareImage, DetailActions, EmptyOrErrorContent } from "@/shared/components";
import { buildRoute, getErrorMessage } from "@/shared/helpers";
import { useAppSelector, useMediaQuery } from "@/shared/hooks";
import { Button } from "@ab.uitools/ui-library/components/Button";
import { SideSheet } from "@ab.uitools/ui-library/components/SideSheet";
import { Spinner } from "@ab.uitools/ui-library/components/Spinner";
import type { ISVGIconProps } from "@ab.uitools/ui-library/components/SVGIcons/types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { CardDetailHistory } from "./CardDetailHistory";
import { CardRenameDialog } from "./CardRenameDialog";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  cardId: string;
}

export const CardDetails = ({ isOpen, onClose, cardId }: Props) => {
  const { t } = useTranslation();
  const featureToggle = useAppSelector((state) => state.featureToggle.toggles);
  const navigate = useNavigate();
  const isDesktop = useMediaQuery();

  const [isOpenRenameDialog, setIsOpenRenameDialog] = useState<boolean>(false);
  const [cardBalance, setCardBalance] = useState<number | null>(null);

  const {
    data: card,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetCardByIdQuery({ cardId });
  const [getCardBalance] = useLazyGetCardBalanceQuery();

  const onCloseRenameDialog = () => setIsOpenRenameDialog(false);

  const onOpenRenameDialog = () => setIsOpenRenameDialog(true);

  useEffect(() => {
    if (!cardId) return;
    getCardBalance({ cardId })
      .unwrap()
      .then((balance: number) => {
        setCardBalance(balance);
      });
  }, [cardId]);

  const navigateToStatementPage = () => {
    const url = buildRoute(AppPaths.CardStatement, { cardId });
    navigate(url);
  };

  const actions = [
    {
      id: 0,
      title: t("general.statement.title"),
      icon: CloudflareAsset.DocumentDeclaration,
      isAvailable: featureToggle.isCardStatementAvailable,
      onClick: () => navigateToStatementPage(),
    }
  ].filter((action) => action.isAvailable);

  return (
    <SideSheet
      position={isDesktop ? SideSheetPosition.Right : SideSheetPosition.Bottom}
      isPositioned={!isDesktop}
      withOverlay={!isDesktop}
      isOpen={isOpen}
      isBodyHidden={false}
      onClose={onClose}
      isWithDivider
      isLoading={isFetching}
      className="detail-side-sheet"
      size="small"
      topLeftActions={
        !isFetching && !isError ? (
          <Button
            type="tertiary"
            size="small"
            className="ml-8"
            onClick={onOpenRenameDialog}
            data-testid="rename-card"
            data-ga-id="rename-card"
            data-ga-action="Open rename card dialog"
            iconProps={{
              Component: (props: ISVGIconProps) => (
                <CloudFlareImage {...props} path={CloudflareAsset.Edit}/>
              ),
            }}/>
        ) : null
      }
      title={card?.name}
      shouldRemoveCallback={false}
      closeOnOutsideClick={false}
    >
      {({ scrollbarContainerRef }) => (
        <>
          {isFetching ? (
            <Spinner isFullScreen={false} size="large"/>
          ) : isError && !isFetching ? (
            <div className="full-width full-height flexbox align-items--center">
              <EmptyOrErrorContent
                isError={true}
                title={t(getErrorMessage(error as IError))}
                action={refetch}
              />
            </div>
          ) : (
            <>
              {card && (
                <>
                  <CardImage
                    balance={cardBalance || 0}
                    number={card.number}
                    holder={card.holder}
                    currency={card.currency}
                    expirationDate={card.expirationDate}
                    cardClassification={card.cardClassification}
                    cardNetwork={card.cardNetwork}
                  />
                  <CardDetailRows
                    card={card}
                    scrollbarContainerRef={scrollbarContainerRef}
                  />
                </>
              )}
              <DetailActions actions={actions} page="Card detail"/>
              {cardId && (
                <CardDetailHistory cardId={cardId}/>
              )}
              <CardRenameDialog
                refetch={refetch}
                cardId={card?.id}
                name={card?.name}
                isOpen={isOpenRenameDialog}
                onClose={onCloseRenameDialog}
              />
            </>
          )}
        </>
      )}
    </SideSheet>
  );
};
